import { useState, useEffect } from 'react';
import CalendarPicker from './CalendarPicker';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, X, Clock } from 'lucide-react';

const formatDate = (date) => {
  if (!date) return '';
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const formatDisplayTime = (timeStr) => {
  const [hour, minute] = timeStr.split(':');
  const date = new Date();
  date.setHours(parseInt(hour), parseInt(minute || 0));
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getCurrentHour = () => new Date().getHours();

const RescheduleOrderModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  vendorConfig,
  isService,
}) => {
  // Parse weekly off days
  const weeklyOffDays = vendorConfig?.weeklyOffDay || '';
  const offDays = weeklyOffDays
    ? weeklyOffDays.split(';').map((day) => day.trim().toLowerCase())
    : [];

  // Check if a date is a weekly off day
  const isWeeklyOffDay = (checkDate) => {
    if (offDays.length === 0) return false;
    const dayName = checkDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return offDays.includes(dayName);
  };

  // Find next available date (skip weekly off days)
  const getNextAvailableDate = (startDate) => {
    let nextDate = new Date(startDate);
    let attempts = 0;
    const maxAttempts = 14; // Check up to 2 weeks ahead

    while (attempts < maxAttempts) {
      if (!isWeeklyOffDay(nextDate)) {
        return nextDate;
      }
      nextDate.setDate(nextDate.getDate() + 1);
      attempts++;
    }
    // If no available date found, return the original date
    return startDate;
  };

  const [selectedDate, setSelectedDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return getNextAvailableDate(tomorrow);
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  // Generate 1-hour slots from shop timing
  const generateHourlySlots = (shopTiming, selectedDate) => {
    if (!shopTiming?.start || !shopTiming?.end) {
      return [];
    }

    const [startHour] = shopTiming.start.split(':').map(Number);
    const [endHour] = shopTiming.end.split(':').map(Number);

    const isToday = formatDate(selectedDate) === 'Today';
    const currentHour = getCurrentHour();

    const slots = [];

    // Determine the effective start hour
    let effectiveStartHour = startHour;
    if (isToday && currentHour >= startHour) {
      effectiveStartHour = currentHour + 1; // Start from next hour if today
    }

    // Handle overnight shops (e.g., 22:00 to 02:00)
    const effectiveEndHour = endHour < startHour ? endHour + 24 : endHour;

    // Generate 1-hour slots
    for (let hour = effectiveStartHour; hour < effectiveEndHour; hour++) {
      const slotStartHour = hour % 24;
      const slotEndHour = (hour + 1) % 24;

      const slotValue = `${String(slotStartHour).padStart(2, '0')}:00-${String(slotEndHour).padStart(2, '0')}:00`;
      const slotLabel = `${formatDisplayTime(`${slotStartHour}:00`)} - ${formatDisplayTime(`${slotEndHour}:00`)}`;

      slots.push({
        value: slotValue,
        label: slotLabel,
      });
    }

    return slots;
  };

  // Calculate available slots based on selected date and vendor config
  useEffect(() => {
    let formattedSlots = [];

    // Check if selected date is a weekly off day
    if (isWeeklyOffDay(selectedDate)) {
      setAvailableSlots([]);
      setSelectedSlot('');
      return;
    }

    if (isService && vendorConfig?.deliverySlots?.service) {
      // For service orders, use configured service slots
      const deliverySlots = vendorConfig.deliverySlots.service || [];
      const isToday = formatDate(selectedDate) === 'Today';

      const slots = isToday
        ? deliverySlots.filter((slot) => {
            const slotHour = parseInt(slot.split(':')[0], 10);
            return slotHour > getCurrentHour();
          })
        : deliverySlots;

      formattedSlots = slots.map((slot) => {
        const [start, end] = slot.split('-');
        return {
          value: slot,
          label: `${formatDisplayTime(start)} - ${formatDisplayTime(end)}`,
        };
      });
    } else if (!isService && vendorConfig?.shopTiming) {
      // For non-service orders, generate 1-hour slots from shop timing
      formattedSlots = generateHourlySlots(vendorConfig.shopTiming, selectedDate);
    }

    setAvailableSlots(formattedSlots);

    // Auto-select first slot if available
    if (formattedSlots.length > 0 && !selectedSlot) {
      setSelectedSlot(formattedSlots[0].value);
    } else if (formattedSlots.length === 0) {
      setSelectedSlot('');
    }
  }, [selectedDate, vendorConfig, isService]);

  const handleConfirm = () => {
    if (availableSlots.length > 0 && !selectedSlot) {
      return; // Don't allow confirmation without slot selection
    }
    onConfirm(selectedDate, selectedSlot);
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isCalendarOpen && (
          <CalendarPicker
            onClose={() => setIsCalendarOpen(false)}
            onSelect={(date) => {
              setSelectedDate(date);
              setIsCalendarOpen(false);
            }}
            selectedDate={selectedDate}
            weeklyOffDays={weeklyOffDays}
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl shadow-2xl px-6 py-4 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Reschedule Order</h3>
              <p className="text-gray-500 text-sm mt-1">Choose a new delivery date</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                {isService ? 'Select New Pickup Date' : 'Select New Delivery Date'}
              </h4>

              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const nextAvailable = getNextAvailableDate(tomorrow);
                    setSelectedDate(nextAvailable);
                  }}
                  className={`flex-1 p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedDate.toDateString() ===
                    getNextAvailableDate(
                      new Date(new Date().setDate(new Date().getDate() + 1))
                    ).toDateString()
                      ? 'bg-orange-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {(() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const nextAvailable = getNextAvailableDate(tomorrow);

                    if (nextAvailable.toDateString() === tomorrow.toDateString()) {
                      return 'Tomorrow';
                    } else {
                      return nextAvailable.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      });
                    }
                  })()}
                </button>

                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className={`flex-1 p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedDate.toDateString() !==
                    getNextAvailableDate(
                      new Date(new Date().setDate(new Date().getDate() + 1))
                    ).toDateString()
                      ? 'bg-orange-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Calendar className="w-4 h-4 mx-auto" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-gray-600 text-sm">Selected Date</p>
                <p className="font-medium text-gray-900">
                  {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Slot Selection - Show for both service and non-service orders */}
            {availableSlots.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  {isService ? 'Select Pickup Time Slot' : 'Select Delivery Time Slot'}
                </h4>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.value}
                      onClick={() => setSelectedSlot(slot.value)}
                      className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedSlot === slot.value
                          ? 'bg-orange-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No slots available message */}
            {availableSlots.length === 0 && (
              <div
                className={`border rounded-lg p-3 ${
                  isWeeklyOffDay(selectedDate)
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <p
                  className={`text-sm text-center ${
                    isWeeklyOffDay(selectedDate) ? 'text-red-800' : 'text-yellow-800'
                  }`}
                >
                  {isWeeklyOffDay(selectedDate)
                    ? `${selectedDate.toLocaleDateString('en-US', { weekday: 'long' })} is a weekly off day. Please choose another date.`
                    : isService
                      ? 'No pickup slots available for the selected date. Please choose another date.'
                      : 'No delivery slots available for the selected date. Please choose another date.'}
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || (availableSlots.length > 0 && !selectedSlot)}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Rescheduling...' : 'Reschedule Order'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default RescheduleOrderModal;
